let trackStats = false;
let stats = {};
function recordStat(profileName, hit) {
	let stat = stats[profileName];

	if (!stat) {
		stat = {calls: 0, hits: 0, size: 0};
		stats[profileName] = stat;
	}

	stat.calls += 1;
	if (hit) stat.hits += 1;
	else stat.size += 1;
}
/**
 * Creates a new function that memoizes the result of the provided `func` function.  When the
 * `resolver` function is specified, its returned value is used as the cache key of the memoized
 * function. By default, the first argument of the memoized function is used as the cache key.
 *
 * The cache is exposed as the `cache` property on the returned memoized function. It can be
 * customized by replacing the `memoize.Cache` constructor with one whose instances implement the
 * same [instance methods](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map#Instance_methods)
 * of `Map`.
 *
 * @function memoize
 * @param {Function} func The function to have its output memoized.
 * @param {Object} options Additional memoize options.
 * @param {Function} options.createCacheKey The function to resolve the cache key. Must return a string.
 * @param {string} options.profileName A unique name to use to track this function and its performance. Optional but highly encouraged to set it
 * @param {string} options.snabbdom Set to true if the function is returning JSX and is using snabbdom-renderer
 * @return {Function} Returns a new memoized function.
 */
export default function memoize(func, createCacheKey, options) {
	let cck = createCacheKey;
	let profileName;
	let snabbdom = false;
	if (options === undefined && typeof createCacheKey === 'object') {
		cck = createCacheKey.createCacheKey;
		profileName = createCacheKey.profileName;
		snabbdom = createCacheKey.snabbdom;
	} else if (typeof options === 'object') {
		cck = createCacheKey || options.createCacheKey;
		profileName = options.profileName;
		snabbdom = options.snabbdom;
	}

	const memoized = function (...args) {
		const key = cck ? cck.apply(this, args) : args[0];
		const {cache} = memoized;

		if (cache.has(key)) {
			if (trackStats) recordStat(profileName, true);

			const result = cache.get(key);
			return snabbdom ? cloneVnode(result) : result;
		}

		if (trackStats) recordStat(profileName, false);

		const result = func.apply(this, args);
		const cacheValue = snabbdom ? cloneVnode(result) : result;
		memoized.cache = cache.set(key, cacheValue) || cache;

		return cacheValue;
	};
	memoized.cache = new (memoize.Cache || Map)();
	return memoized;
}

memoize.Cache = Map;

memoize.collectStats = function collectStats(value = true) {
	trackStats = value;
};

memoize.clearStats = function clearStats() {
	stats = {};
};

memoize.getStats = function getStats() {
	return Object.keys(stats).reduce((obj, current) => {
		const {hits, calls} = stats[current];
		const stat = {
			...stats[current],
			usage: calls ? `${((hits / calls) * 100).toFixed(4)}%` : '0.0000%'
		};
		return {
			...obj,
			[current]: stat
		};
	}, {});
};

function cloneVnode(vnode) {
	if (!vnode) return vnode;
	return {
		children:
			vnode.children !== undefined ? vnode.children.map(child => cloneVnode(child)) : undefined,
		data: vnode.data,
		elm: undefined,
		key: vnode.key,
		sel: vnode.sel,
		text: vnode.text
	};
}
